import { useState } from "react";
import { Plus, X } from "lucide-react";
import { FormSection } from "../types/forms";

interface AddSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (section: FormSection) => void;
  existingSections: FormSection[];
}

const sectionOptions = [
  {
    type: "contact",
    title: "Contact Details",
    description: "Collect contact information",
  },
  {
    type: "medical",
    title: "Medical Conditions",
    description: "Select applicable medical conditions",
  },
  {
    type: "question",
    title: "General Question",
    description: "Add custom questions with various answer types",
  },
  {
    type: "policy",
    title: "Privacy Policy",
    description: "Standard privacy policy text block",
  },
  {
    type: "disclaimer",
    title: "Client Disclaimer",
    description: "Standard client disclaimer text block",
  },
  {
    type: "heading",
    title: "Heading",
    description: "To name section of your form",
  },
  {
    type: "text",
    title: "Text Block",
    description: "For instructions or terms and conditions",
  },
  // {
  //   type: "marketing",
  //   title: "Marketing Details",
  //   description: "Opt-in for SMS and Email notifications",
  // },
];

export default function AddSectionModal({
  isOpen,
  onClose,
  onAdd,
  existingSections,
}: AddSectionModalProps) {
  const [searchQuery, setSearchQuery] = useState("");

  if (!isOpen) return null;

  const handleAdd = (type: string, title: string, description?: string) => {
    const getNextSectionId = (existingSections: FormSection[]) => {
      const ids = existingSections.map((s) => Number(s.id));
      return ids.length ? Math.max(...ids) + 1 : 1;
    };

    const newSection: FormSection = {
      id: getNextSectionId(existingSections),
      type: type as FormSection["type"],
      title,
    };

    onAdd(newSection);
    onClose();
  };

  const filteredOptions = sectionOptions.filter(
    (opt) =>
    (opt.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opt.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-md rounded-xl shadow-lg p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Add Form Field
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search sections..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-md text-sm"
        />

        {/* Options */}
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map(({ type, title, description }) => (
              <button
                key={type}
                onClick={() => handleAdd(type, title)}
                className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-purple-50 transition"
              >
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">{title}</p>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
                <Plus size={18} className="text-[#885ABB]" />
              </button>
            ))
          ) : (
            <p className="text-center text-gray-500 text-sm">
              No sections available
            </p>
          )}
        </div>

        {/* Footer */}
        <button
          onClick={onClose}
          className="w-full text-[#885ABB] font-medium py-2 mt-4 hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
